/**
 * Receipt configuration constants
 */

export type MarginPreset = "THERMAL" | "SCREEN" | "PRINT" | "BULK_PRINT";

export const RECEIPT_CONFIG = {
    // Physical dimensions
    THERMAL_WIDTH_MM: 80,
    THERMAL_HEIGHT_MM: 200,

    // Margins (left, right)
    MARGINS: {
        THERMAL: { left: 3, right: 3, unit: "mm" as const },
        SCREEN: { left: 16, right: 16, unit: "px" as const },
        PRINT: { left: 12, right: 12, unit: "mm" as const },
        BULK_PRINT: { left: 20, right: 20, unit: "mm" as const },
    },

    // Content width calculations
    getContentWidth(format: MarginPreset): number {
        const margins = this.MARGINS[format];
        const totalWidth = format === "THERMAL" ? this.THERMAL_WIDTH_MM : 300;
        return totalWidth - margins.left - margins.right;
    },

    // Header layout calculations
    getHeaderLayout(format: MarginPreset = "THERMAL") {
        const contentWidth = this.getContentWidth(format);
        const logoWidth =
            format === "THERMAL" ? this.LOGO.WIDTH_MM : this.LOGO.WIDTH_PX;
        const totalParts = this.HEADER.TOTAL_PARTS;
        const logoParts = this.HEADER.LOGO_PARTS;
        const contentParts = this.HEADER.CONTENT_PARTS;

        const logoSectionWidth = (logoParts / totalParts) * contentWidth;
        const contentSectionWidth = (contentParts / totalParts) * contentWidth;

        return {
            logoWidth: Math.min(logoWidth, logoSectionWidth - 2), // Leave 2mm/px margin
            contentWidth: contentSectionWidth - 2, // Leave 2mm/px margin
            logoX: this.MARGINS[format].left,
            contentX: this.MARGINS[format].left + logoSectionWidth + 2,
        };
    },

    // Typography
    FONT: {
        FAMILY: "Courier New, monospace",
        SIZE: {
            HEADER: 12,
            NORMAL: 8,
            SMALL: 6,
            LARGE: 10,
        },
    },

    // Spacing
    SPACING: {
        LINE_HEIGHT: 3, // mm for PDF
        SECTION_GAP: 4, // mm for PDF
        ITEM_GAP: 1, // mm for PDF
    },

    // Logo configuration
    LOGO: {
        URL: "/images/logo192.png", // Actual logo file
        WIDTH_MM: 15, // Logo width in mm
        HEIGHT_MM: 15, // Logo height in mm (square for logo)
        WIDTH_PX: 60, // Logo width in pixels for screen
        HEIGHT_PX: 60, // Logo height in pixels for screen
        RATIO: 1 / 4, // Logo takes 1/4 of header width
    },

    // Centralized shop information
    SHOP_INFO: {
        NAME: "CATELYN PET SHOP",
        ADDRESS: "Bottola Railgate, Sector 4, Uttara",
        PHONE: "01800-072-072",
        EMAIL: "catelynpetshop@gmail.com",
        WEBSITE: "www.catelynpetshop.com",
    },

    // Header layout configuration
    HEADER: {
        RATIO: { LOGO: 1, CONTENT: 3 }, // 1:3 ratio (logo:content)
        TOTAL_PARTS: 4, // Total parts for ratio calculation
        LOGO_PARTS: 1, // Logo takes 1 part
        CONTENT_PARTS: 3, // Content takes 3 parts
    },
} as const;
